import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State } from '@stencil/core'
import { Method } from '@stencil/core/internal'
import Axios from 'axios'
import { debounce } from 'lodash-es'
import { AppSharedPropsStore } from 'src/app'
import { asyncToast, flashToast } from 'src/app/toast'
import { CallDeleteDialog } from 'src/call/delete_dialog'
import { crmSyncInProgress, currentCrm, syncToCrm } from 'src/crm/state'
import { getFolderName } from 'src/folder/utils'
import { getPlaylistName } from 'src/playlist/utils'
import { ISharing } from 'src/share/types'
import { SyncIndicator } from 'src/ui/sync-indicator/sync-indicator'
import { copyText } from 'src/utils/clipboard'
import { storeForCrm } from 'src/utils/crm'
import { classes } from 'src/utils/css'

import { copyToClipboardWithProvider } from '../../../../call/clipboard/clipboard'
import { ICall, ICallComment, IContact, INote, INoteClip, IQuestionClip } from '../../../../call/types'
import { Menu, MenuItem } from '../../../../ui/menu/menu'
import { CommentStore } from '../comment'
import { CallPage } from '../detail'
import {
  CallStore,
  clipIsProcessing,
  downloadFullVideo,
  getActionItemClips,
  getAnnotations,
  getQuestionClips,
  getScreenShareClips,
  NotesStore,
} from '../state'
import { CallActionItemDetectionStatus } from '../types'
import {
  ActionItemsClipboardMenu,
  AiActionItemBanner,
  CallMenuButton,
  ChatMessage,
  ClipboardStore,
  NoteSection,
  PlaceholderClip,
  QuestionNoteSection,
} from './ui'

@Component({
  tag: 'page-call-detail-notes',
  assetsDirs: ['assets'],
})
export class Notes {
  @Element() el: HTMLPageCallDetailNotesElement

  @Prop() showHeader = true
  @Prop() showV2Layout = false
  @Prop() editNote: INote
  @Prop() questions: CallPage['questions']
  @Prop() allContacts: IContact[] = []
  @Prop() activateDownloadUrl: string
  @Prop() clipboardActionItemsUrl: string
  @Prop() recordingsUrl = '/'
  @Prop() customizeUrl = '/customize'
  @Prop() comments: ICallComment[]
  @Prop() sharing: ISharing
  @Prop() showCommentsAnimation = false
  @Prop() deletedAutoGenerated = false
  @Prop() firstPostMonthlyFreeUsage = false
  @Event() delete: EventEmitter<INote>
  @Event() deleteAllAutoGenerated: EventEmitter
  @Event() edit: EventEmitter<INote>
  @Event() salesforceSent: EventEmitter
  @Event({ bubbles: false }) sharedRecordingWithAttendees: EventEmitter

  @State() downloadingMp4 = false
  @State() showAddToPlaylistMenu = false
  @State() recordingSent = false
  @State() callToBeDeleted: ICall = null
  @State() showTeamEditionRequestPaidSeat = false
  @State() showShareDialog = false

  menuEl: HTMLUiTooltipElement

  @Listen('keydown')
  onKeyDown(event: KeyboardEvent) {
    const el = event.target as HTMLElement
    if (el.hasAttribute('data-note-editor')) {
      const editors = Array.from<HTMLElement>(document.querySelectorAll('[data-note-editor]'))
      if (['Enter', 'Tab'].includes(event.key)) {
        event.preventDefault()
        const next = event.shiftKey
          ? editors[editors.indexOf(el) - 1] || editors[editors.length - 1]
          : editors[editors.indexOf(el) + 1] || editors[0]
        const { top: scrollTop, bottom: scrollBottom } = this.scrollEl.getBoundingClientRect()
        const { top: inputTop, bottom: inputBottom } = next.getBoundingClientRect()
        if (inputTop > scrollBottom || inputTop < scrollTop || inputBottom > scrollBottom || inputBottom < scrollTop) {
          this.scrollEl.scrollToElement(next).then(() => {
            next.focus({ preventScroll: true })
          })
        } else {
          next.focus({ preventScroll: true })
        }
      }
    }
  }

  @Listen('keyup')
  onKeyUp(event: KeyboardEvent) {
    const el = event.target as HTMLElement
    if (el.hasAttribute('data-note-editor') && ['Enter', 'Tab'].includes(event.key)) {
      event.preventDefault()
    }
  }

  scrollEl: HTMLUiScrollareaElement

  @Method()
  async copyActionItems(targetProvider: string) {
    await copyToClipboardWithProvider(this.clipboardActionItemsUrl, targetProvider)
  }

  componentWillLoad() {
    this.recordingSent = CallStore.state.call.sharedRecordingWithAttendees
    NotesStore.onChange('noteClips', () => this.onUpdateNoteClips())
    CallStore.onChange('questions', () => this.onUpdateQuestionClips())
    CallStore.onChange('actionItemDetection', () => this.onUpdateActionItemDetection())
    this.onUpdateNoteClips()
    this.onUpdateQuestionClips()
    this.onUpdateActionItemDetection()
  }

  @State() annotations: INoteClip[]
  @State() bookmarks: INoteClip[]
  @State() actionItems: INoteClip[]
  @State() screenShareClips: INoteClip[]
  @State() nonGuestQuestions: IQuestionClip[]
  @State() guestQuestions: IQuestionClip[]

  onUpdateNoteClips = debounce(
    () => {
      this.annotations = getAnnotations().sort((a, b) => a.start_time - b.start_time)
      this.actionItems = getActionItemClips()
      this.screenShareClips = getScreenShareClips()
    },
    1000,
    { leading: true, trailing: true }
  )

  onUpdateQuestionClips = debounce(
    () => {
      this.nonGuestQuestions = getQuestionClips().filter((questionClip) => !questionClip.from_guest)
      this.guestQuestions = getQuestionClips().filter((questionClip) => questionClip.from_guest)
    },
    1000,
    { leading: true, trailing: true }
  )

  @State() actionItemDetectionStatus: CallActionItemDetectionStatus = null

  onUpdateActionItemDetection = debounce(
    () => {
      this.actionItemDetectionStatus = CallStore.state.actionItemDetection?.status
    },
    1000,
    { leading: true, trailing: true }
  )

  showExtractingActionItems() {
    return this.actionItemDetectionStatus === 'generating' || this.actionItemDetectionStatus === 'will-generate'
  }

  showExtractActionItemsButton() {
    return (
      this.actionItemDetectionStatus === 'may-generate' ||
      this.actionItemDetectionStatus === 'may-generate-later' ||
      this.actionItemDetectionStatus === 'stopped'
    )
  }

  showActionItemsError() {
    return this.actionItemDetectionStatus === 'stopped'
  }

  showNoActionItemsDetected() {
    return this.actionItemDetectionStatus === 'completed' && this.actionItems.length === 0
  }

  downloadUnavailableTooltip() {
    // This can be evaluated after Inertia-based navigation to another page, in which case CallStore is undefined
    if (CallStore?.state?.call?.host?.company?.name) {
      return `Blocked by ${CallStore.state.call.host.company.name} IT`
    }
    return 'Blocked by your company IT'
  }

  hasAutoGeneratedNotes() {
    return this.actionItems.some((actionItem) => actionItem.note.auto_generated)
  }

  showDeleteAutoGeneratedBanner() {
    return this.deletedAutoGenerated || (CallStore.state.access === 'host' && this.hasAutoGeneratedNotes())
  }

  render() {
    // HACK: When navigating away from call details page, we reset CallStore.
    // Due to some messy state changes, this component is re-rendered during that navigation.
    // If the rerender occurs after the call has been reset, we often receive errors trying to
    // access properties on the store's call that no longer exists.
    if (Object.keys(CallStore.state.call).length < 1) {
      return null
    }
    const showCrmSyncButton = CallStore.state.showCrmSync
    const downloadUnavailable = !(CallStore.state.activateDownloadUrl || CallStore.state.downloadVideoUrl)

    return (
      <Host class='flex flex-col'>
        <ui-scrollarea
          ref={(el) => (this.scrollEl = el)}
          overflowX={false}
          scrollOffsetY={120}
          shadowColor='transparent'
        >
          <div class='flex flex-col px-4 pt-5 pb-8 spacing-y-5'>
            {CallStore.state.playlistMetadata?.nextPlaylistItem && (
              <div class='flex flex-col pb-15px mb-15px border-b border-gray-700 spacing-y-15px'>
                <span class='font-semibold' style={{ fontSize: '20px' }}>
                  Up Next
                </span>
                <a class='flex flex-col' href={CallStore.state.playlistMetadata.nextPlaylistItem.actions.showUrl}>
                  <playlist-item-thumbnail
                    playlistItem={CallStore.state.playlistMetadata.nextPlaylistItem}
                    size='lg'
                    allowRemove={
                      CallStore.state.playlistMetadata.currentPlaylistItemSortOrder !==
                      CallStore.state.playlistMetadata.nextPlaylistItem.sortOrder
                    }
                  />
                </a>
              </div>
            )}
            {CallStore.state.showMisattributedTranscriptCuesBanner && (
              <div class='flex flex-col mb-5 spacing-y-1'>
                <section class='flex justify-between spacing-x-8px'>
                  <div class='py-1 px-2 text-md font-semibold text-info-warn bg-info-warn bg-opacity-10 rounded-md border border-info-warn hover:border-info-warn border-opacity-5'>
                    <span class='font-extrabold'>Transcription Note:</span> Speaker identification unavailable due to
                    incident at time of recording. Apologies.
                  </div>
                </section>
              </div>
            )}
            <video-call-chip
              onTitleEdited={({ detail }) => {
                Axios.put(CallStore.state.call.updateUrl, { topic: detail })
              }}
              call={CallStore.state.call}
              currentUser={CallStore.state.readonly ? null : AppSharedPropsStore.state.currentUser}
              rating={!CallStore.state.isLimitedView}
              date
              large
              companyName={false}
              dateColor='gray-90'
              folders={
                CallStore.state.call.folders?.length > 0 ||
                !!CallStore.state.call.folderActions?.createUrl ||
                !!CallStore.state.call.folderActions?.addToFolderMenuUrl
              }
              onAddedToFolder={(e) => {
                const folder = e.detail
                CallStore.state.call = {
                  ...CallStore.state.call,
                  folders: [...CallStore.state.call.folders, folder],
                }
              }}
              onFailedAddToFolder={(e) => {
                flashToast({
                  type: 'alert',
                  text: `Failed to add call to ${getFolderName(e?.detail)}`,
                })
              }}
              onRemoveFromFolder={async ({ detail }) => {
                try {
                  await Axios.delete(detail.actions.removeUrl)
                  CallStore.state.call = {
                    ...CallStore.state.call,
                    folders: CallStore.state.call.folders.filter((folder) => folder.id !== detail.id),
                  }
                } catch (ex) {
                  flashToast({ type: 'alert', text: `Failed to remove from ${getFolderName(detail)}` })
                }
              }}
            />

            <div class='flex flex-col spacing-y-8px'>
              <section class='flex justify-between spacing-x-8px'>
                <page-call-detail-share-menu
                  class='flex-1'
                  showShareDialog={this.showShareDialog}
                  sharing={this.sharing}
                  teamSharingRequiresUpdate={!!CallStore.state.call.requestTeamPaidSeatUrl}
                  callId={CallStore.state.call.id}
                  onTriggeredUpgradeRequired={() => {
                    if (CallStore.state.call.requestTeamPaidSeatUrl) {
                      this.showTeamEditionRequestPaidSeat = true
                    }
                  }}
                  onToggledShareDialog={() => (this.showShareDialog = !this.showShareDialog)}
                />
                {showCrmSyncButton && (
                  <div class='relative'>
                    <CallMenuButton
                      class='flex-1'
                      onClick={async () => {
                        storeForCrm(currentCrm()).state.manualContactSyncRequested = true
                        await syncToCrm(true)
                      }}
                      disabled={crmSyncInProgress()}
                      useRightPadding={false}
                    >
                      <span class='pr-14px'>Sync to CRM</span>
                      <div class='flex relative items-center p-4px px-1 -m-2px -mr-1 ml-1'>
                        {crmSyncInProgress() ? (
                          <SyncIndicator />
                        ) : (
                          <ui-icon icon={currentCrm()} class='text-xl opacity-50' />
                        )}
                      </div>
                    </CallMenuButton>
                  </div>
                )}
                {(CallStore.state.call.delete_url || this.activateDownloadUrl) && (
                  <button class='flex justify-between items-center p-1 pr-2 pl-2 text-base font-bold text-glyph-light hover:text-surface-base bg-glyph-light bg-opacity-10 rounded-md cursor-pointer whitespace-no-wrap'>
                    <ui-icon class='-mx-8px text-xl text-surface-highest' icon='three-dots-vertical'></ui-icon>
                    <Menu ref={(el) => (this.menuEl = el)} allowClick={false} contentWidth='48'>
                      {!this.showAddToPlaylistMenu ? (
                        <Fragment>
                          {!CallStore.state.readonly &&
                            !!CallStore.state.call.callPlaylistActions?.createUrl &&
                            !!CallStore.state.call.callPlaylistActions?.indexUrl && (
                              <MenuItem
                                icon='add-to-playlist'
                                onClick={() => {
                                  this.showAddToPlaylistMenu = true
                                }}
                              >
                                Add to Playlist
                              </MenuItem>
                            )}
                          {this.activateDownloadUrl && (
                            <MenuItem
                              onClick={async () => {
                                this.downloadingMp4 = true
                                await downloadFullVideo()
                                this.downloadingMp4 = false
                              }}
                              disabled={downloadUnavailable || this.downloadingMp4}
                              class='hover:text-link'
                              icon='download'
                              disabledReason={
                                downloadUnavailable
                                  ? this.downloadUnavailableTooltip()
                                  : this.downloadingMp4 && 'Generating download...'
                              }
                            >
                              Download Video
                            </MenuItem>
                          )}
                          {CallStore.state.call.delete_url && (
                            <MenuItem
                              class='hover:text-info-error'
                              icon='trash'
                              onClick={() => (this.callToBeDeleted = CallStore.state.call)}
                            >
                              Delete Call
                            </MenuItem>
                          )}
                        </Fragment>
                      ) : (
                        <add-to-playlist-menu
                          createPlaylistUrl={CallStore.state.call.callPlaylistActions.createUrl}
                          item={{ id: CallStore.state.call.id, type: 'call' }}
                          listPlaylistsUrl={CallStore.state.call.callPlaylistActions.indexUrl}
                          onMenuClosed={() => {
                            this.showAddToPlaylistMenu = false
                          }}
                          onAdded={(e) => {
                            flashToast({ type: 'success', text: `Added call to ${getPlaylistName(e?.detail)}` })
                            this.showAddToPlaylistMenu = false
                            this.menuEl?.close()
                          }}
                          onFailed={(e) => {
                            flashToast({
                              type: 'alert',
                              text: `Failed to add call to ${getPlaylistName(e?.detail)}`,
                            })
                            this.showAddToPlaylistMenu = false
                            this.menuEl?.close()
                          }}
                        />
                      )}
                    </Menu>
                  </button>
                )}
              </section>

              {this.sharing && CallStore.state.call.updateTeamViewVisibilityUrl && (
                <div class='flex relative'>
                  <team-select-share-visibility
                    class='w-full text-base'
                    visibility={CallStore.state.call.teamVisibility.override}
                    teamRoleLabel={CallStore.state.call.teamVisibility.teamRole}
                    lowestVisibilityAllowed={CallStore.state.call.teamVisibility.maxVisibilityTeamFolder?.visibility}
                    disabled={!CallStore.state.call.updateTeamViewVisibilityUrl}
                    onSelected={async ({ detail: visibility }) => {
                      if (visibility === 'visible_to_multiple_team_roles') {
                        this.showShareDialog = true
                        return
                      }
                      const previousVisibilityOverride = CallStore.state.call.teamVisibility.override

                      try {
                        CallStore.state.call = {
                          ...CallStore.state.call,
                          teamVisibility: { ...CallStore.state.call.teamVisibility, override: visibility },
                        }
                        await Axios.put(CallStore.state.call.updateTeamViewVisibilityUrl, { visibility })
                      } catch (error) {
                        CallStore.state.call = {
                          ...CallStore.state.call,
                          teamVisibility: {
                            ...CallStore.state.call.teamVisibility,
                            override: previousVisibilityOverride,
                          },
                        }
                        flashToast({ type: 'alert', text: 'Failed to update visibility' })
                      }
                    }}
                  />
                  {CallStore.state.call.requestTeamPaidSeatUrl && (
                    <div
                      class='absolute z-10 w-full h-full cursor-pointer'
                      onClick={
                        CallStore.state.call.requestTeamPaidSeatUrl
                          ? () => {
                              this.showTeamEditionRequestPaidSeat = true
                            }
                          : null
                      }
                    />
                  )}
                </div>
              )}
            </div>

            {!CallStore.state.isLimitedView && (
              <Fragment>
                {!!CallStore.state.attendeeMetadata?.attendees?.length && (
                  <NoteSection
                    label='Attendees'
                    color='surface-highest'
                    renderHeaderContent={() =>
                      CallStore.state.call.shareRecordingWithAttendeesUrl && (
                        <div class='items-end ml-auto'>
                          {this.recordingSent ? (
                            <span class='flex items-center h-5 text-xs font-semibold text-info-warn normal-case'>
                              <ui-icon icon='envelope-check' class='-mt-2px mr-1 text-base'></ui-icon>
                              <span>Recording Sent</span>
                            </span>
                          ) : (
                            <button
                              class='flex items-center pr-1 pl-8px h-5 text-xs font-semibold text-link hover:text-surface-base normal-case bg-link bg-opacity-10 hover:bg-opacity-100 rounded transition duration-200'
                              onClick={async () => {
                                if (!this.recordingSent) {
                                  await Axios.post(CallStore.state.call.shareRecordingWithAttendeesUrl).then(() => {
                                    this.recordingSent = true
                                    CallStore.state.call.sharedRecordingWithAttendees = true
                                    this.sharedRecordingWithAttendees.emit()
                                  })
                                }
                              }}
                            >
                              <span>Send Recording</span>
                              <ui-icon icon='reply' class='-mt-2px ml-1 text-xl opacity-50'></ui-icon>
                              <ui-tooltip>Email recording to all attendees</ui-tooltip>
                            </button>
                          )}
                        </div>
                      )
                    }
                  >
                    <page-call-detail-attendees attendees={CallStore.state.attendeeMetadata.attendees} />
                  </NoteSection>
                )}
                {(CallStore.state.attendeeMetadata?.crmOpportunity || CallStore.state.relatedCall) && (
                  <NoteSection label='Related' color='surface-highest'>
                    <ul class='flex flex-col text-base spacing-y-2'>
                      {CallStore.state.attendeeMetadata?.crmOpportunity && (
                        <li>
                          <page-call-detail-crm-opportunity
                            opportunity={CallStore.state.attendeeMetadata.crmOpportunity}
                            data-page-call-detail-crm-opportunity
                          />
                        </li>
                      )}
                      {CallStore.state.relatedCall && (
                        <li>
                          <page-call-detail-related-call call={CallStore.state.relatedCall} />
                        </li>
                      )}
                    </ul>
                  </NoteSection>
                )}
              </Fragment>
            )}

            {(this.actionItems.length > 0 ||
              this.showExtractingActionItems() ||
              this.showExtractActionItemsButton() ||
              this.showNoActionItemsDetected()) && (
              <NoteSection
                bookmark={CallStore.state.bookmarks.find((b) => b.action_item)}
                label='Action items'
                color='surface-highest'
                callId={CallStore.state.call.id}
              >
                {this.actionItems.length > 0 && CallStore.state.followUpEmail && (
                  <div class='flex justify-between mb-2 spacing-x-8px'>
                    <button
                      class='flex justify-between items-center p-1 pr-2 pl-2 text-base font-bold text-link hover:text-surface-base normal-case bg-link hover:bg-link bg-opacity-10 rounded-md cursor-pointer spacing-x-8px whitespace-no-wrap'
                      onClick={() => {
                        if (ClipboardStore.state.actionItemsProvider) {
                          this.copyActionItems(ClipboardStore.state.actionItemsProvider.name)
                        }
                      }}
                    >
                      <span>Copy for</span>
                      {ClipboardStore.state.actionItemsProvider ? (
                        <div class='flex relative items-center p-4px px-1 -m-2px -mr-1 bg-surface-base rounded pointer-events-auto'>
                          <img src={ClipboardStore.state.actionItemsProvider.iconUrl} class='w-4 h-4' />
                          <ActionItemsClipboardMenu onProviderSelected={(p) => this.copyActionItems(p.name)} />
                        </div>
                      ) : (
                        <Fragment>
                          <div class='flex relative items-center p-4px px-1 -m-2px -mr-1 rounded pointer-events-auto'>
                            <span style={{ lineHeight: '20px' }}>&nbsp;…</span>
                          </div>
                          <ActionItemsClipboardMenu onProviderSelected={(p) => this.copyActionItems(p.name)} />
                        </Fragment>
                      )}
                    </button>
                    {!!CallStore.state.followUpEmail?.actions?.generateUrl && (
                      <button
                        class='flex flex-1 justify-between items-center p-1 pr-2 pl-2 text-base font-bold text-link hover:text-surface-base normal-case bg-link hover:bg-link bg-opacity-10 rounded-md cursor-pointer spacing-x-8px whitespace-no-wrap'
                        onClick={() => {
                          const copyPromise = new Promise<void>((resolve) => {
                            const off = CallStore.onChange('followUpEmail', async () => {
                              if (CallStore.state.followUpEmail.isReady) {
                                off()
                                // The initial asyncToast ("Generating Email using AI") steals
                                // focus from the document, which causes copyText to fail, unless
                                // we give focus back to our component (before calling copyText)
                                this.el.focus()
                                await copyText(CallStore.state.followUpEmail.emailBody, true)
                                resolve()
                              }
                            })
                            CallStore.state.followUpEmail = { ...CallStore.state.followUpEmail, isReady: false }
                            Axios.post(CallStore.state.followUpEmail.actions.generateUrl)
                          })
                          asyncToast(
                            copyPromise,
                            'Generating Email using AI…',
                            'Copied',
                            'Error copying follow-up email'
                          )
                          return copyPromise
                        }}
                      >
                        <span>Copy Follow-up Email</span>
                        <ui-icon icon='envelope' />
                      </button>
                    )}
                  </div>
                )}

                <ul>
                  {this.actionItems.map((clip) =>
                    clipIsProcessing(clip) ? (
                      <PlaceholderClip
                        bookmark={clip.note.bookmark}
                        mockSeconds={CallStore.state.call.processing_duration}
                      />
                    ) : (
                      <li>
                        <call-detail-notes-note-clip
                          key={`call-detail-notes-note-clip-${clip.id}`}
                          clip={clip}
                          showMenu={!CallStore.state.readonly}
                          readonly={CallStore.state.readonly}
                          highlit={false}
                          onEdit={({ detail }) => this.edit.emit(detail)}
                          onDelete={({ detail }) => this.delete.emit(detail)}
                        />
                      </li>
                    )
                  )}
                </ul>

                {this.showActionItemsError() && (
                  <div class='mb-1 italic text-red'>Something went wrong, please try again</div>
                )}

                {this.showExtractingActionItems() ? (
                  <span class='flex items-center py-10px px-12px mt-1 text-base font-bold text-brand-base bg-surface-low rounded-md shadow-lg pointer-events-auto spacing-x-12px'>
                    <div class='flex justify-center items-center w-4 h-4'>
                      <ui-spinner strokeWeight='3px' />
                    </div>
                    <span class='whitespace-nowrap'>Extracting Action Items ...</span>
                  </span>
                ) : this.firstPostMonthlyFreeUsage ? (
                  <button
                    class={`
                      flex justify-between items-center py-10px px-12px mt-1 text-base font-bold rounded-md shadow-lg pointer-events-auto spacing-x-1
                      text-gray-70 bg-gray-70 bg-opacity-10 cursor-default
                    `}
                    disabled={true}
                  >
                    <span class='whitespace-nowrap'>Extract Action Items from Transcript</span>
                    <div class='flex justify-center items-center w-4 h-4'>
                      <ui-icon class='opacity-50' icon='robot' />
                    </div>
                    <ui-infotip>Free usage exhausted for this month - please upgrade</ui-infotip>
                  </button>
                ) : this.showExtractActionItemsButton() ? (
                  <button
                    class={`
                      flex justify-between items-center py-10px px-12px mt-1 text-base font-bold rounded-md shadow-lg pointer-events-auto spacing-x-1
                      ${classes(
                        CallStore.state.actionItemDetection?.actions?.detectUrl,
                        'text-brand-base bg-brand-base bg-opacity-10 cursor-pointer',
                        'text-gray-70 bg-gray-70 bg-opacity-10 cursor-default'
                      )}
                    `}
                    disabled={!CallStore.state.actionItemDetection?.actions?.detectUrl}
                    onClick={async () => {
                      if (CallStore.state.actionItemDetection.isEligible) {
                        try {
                          await Axios.post(CallStore.state.actionItemDetection.actions.detectUrl)
                          this.actionItemDetectionStatus = 'generating'
                        } catch (ex) {
                          if (ex.response && ex.response.status === 403) {
                            CallStore.state.requiredPlan =
                              ex.response.data?.requiredPlan ||
                              AppSharedPropsStore?.state?.currentUser?.requiredAdvancedAiPlan
                          } else {
                            flashToast({ type: 'alert', text: 'Please try again' })
                          }
                        }
                      } else {
                        CallStore.state.requiredPlan = AppSharedPropsStore?.state?.currentUser?.requiredAdvancedAiPlan
                      }
                    }}
                  >
                    <span class='whitespace-nowrap'>Extract Action Items from Transcript</span>
                    <div class='flex justify-center items-center w-4 h-4'>
                      <ui-icon class='opacity-50' icon='robot' />
                    </div>
                  </button>
                ) : this.showNoActionItemsDetected() ? (
                  <span class='flex items-center py-16px px-27px mt-1 text-xlarge italic font-medium text-glyph-lighter bg-surface-low rounded-md shadow-lg'>
                    <span>None detected. Add manually on transcript tab</span>
                  </span>
                ) : null}

                {this.showDeleteAutoGeneratedBanner() && (
                  <AiActionItemBanner
                    class='mt-2'
                    onDelete={() => this.deleteAllAutoGenerated.emit()}
                    deleted={this.deletedAutoGenerated}
                    customizeUrl={this.customizeUrl}
                  />
                )}
              </NoteSection>
            )}

            {(CommentStore.state.selectedCue || this.comments.length > 0) && (
              <page-call-detail-comments-panel comments={this.comments} />
            )}

            {this.annotations.length > 0 && (
              <page-call-detail-note-annotations-panel
                clips={this.annotations}
                onDelete={({ detail }) => this.delete.emit(detail)}
                onEdit={({ detail }) => this.edit.emit(detail)}
              />
            )}

            {this.screenShareClips.length > 0 && (
              <NoteSection label='Screen Sharing' color='surface-highest'>
                {this.screenShareClips.map((clip) =>
                  clipIsProcessing(clip) ? (
                    <PlaceholderClip
                      bookmark={clip.note.bookmark}
                      mockSeconds={CallStore.state.call.processing_duration}
                    />
                  ) : (
                    <li>
                      <call-detail-notes-note-clip
                        key={`call-detail-notes-note-clip-${clip.id}`}
                        clip={clip}
                        showMenu={!CallStore.state.readonly}
                        readonly={true}
                        highlit={false}
                        onEdit={({ detail }) => this.edit.emit(detail)}
                        onDelete={({ detail }) => this.delete.emit(detail)}
                      />
                    </li>
                  )
                )}
              </NoteSection>
            )}
            {CallStore.state.chatMessages.length > 0 && (
              <NoteSection label='Chat Messages' color='surface-highest'>
                {CallStore.state.chatMessages.map((msg) => (
                  <ChatMessage
                    id={msg.id}
                    senderName={msg.sender_name}
                    startTime={msg.start_time}
                    content={msg.content}
                  />
                ))}
              </NoteSection>
            )}
            <QuestionNoteSection
              label='Your Questions'
              clips={CallStore.state.isLimitedView ? this.guestQuestions : this.nonGuestQuestions}
            />
            <QuestionNoteSection
              label='Their Questions'
              clips={CallStore.state.isLimitedView ? this.nonGuestQuestions : this.guestQuestions}
            />
          </div>
        </ui-scrollarea>
        {this.callToBeDeleted && (
          <CallDeleteDialog
            call={this.callToBeDeleted}
            onClosed={() => {
              this.callToBeDeleted = null
            }}
            nextUrl={this.recordingsUrl}
          />
        )}
        {this.showTeamEditionRequestPaidSeat && CallStore.state.call.requestTeamPaidSeatUrl && (
          <team-edition-request-paid-seat
            onCancel={() => {
              this.showTeamEditionRequestPaidSeat = false
            }}
            requestTeamPaidSeatUrl={CallStore.state.call.requestTeamPaidSeatUrl}
          />
        )}
      </Host>
    )
  }
}
